import React, { useState, useEffect } from 'react'
import FileUpload from './FileUpload.jsx'

export default function AdminImportsForm({ onSubmit, disabled, onUserChange, onDocumentUpload, uploadingDocs = {}, initialValues, existingDocuments = {}, resetKey, customerInfo,  ensureDraft}) {
  const defaultFormData = {
    city: 'LHR',
    transportMode: 'air',
    containerType: 'LCL',
    port: '',
    clearingAgentNum: '',
    files: {},
  }

  const [formData, setFormData] = useState(defaultFormData)
  const [errors, setErrors] = useState({})
  const [uploadSuccess, setUploadSuccess] = useState({})
  const [uploadProgress, setUploadProgress] = useState({})

  const updateFormData = (updater, options = { notify: true }) => {
    setFormData((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (options.notify && onUserChange) onUserChange(next)
      return next
    })
  }

  useEffect(() => {
    if (initialValues) updateFormData(initialValues, { notify: false })
  }, [initialValues])

  useEffect(() => {
    if (resetKey) {
      updateFormData(defaultFormData, { notify: false })
      setErrors({})
      setUploadSuccess({})
      setUploadProgress({})
    }
  }, [resetKey])

  const renderProgress = (fileType) => {
    const progress = uploadProgress[fileType]
    if (progress == null) return null
    const isComplete = progress >= 100
    return (
      <div style={{ marginTop: '6px' }}>
        <div style={{ height: 6, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: isComplete ? '#16a34a' : '#2563eb', transition: 'width 0.2s ease' }} />
        </div>
        <div style={{ fontSize: 12, color: isComplete ? '#16a34a' : '#2563eb', marginTop: 4 }}>{isComplete ? 'Upload complete' : `Uploading... ${progress}%`}</div>
      </div>
    )
  }

  const commitUserChanges = (next) => {
    if (onUserChange) onUserChange(next ?? formData)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const newErrors = {}

    if (!formData.files.commonInvoices) newErrors.commonInvoices = 'Commercial Invoices is required'
    if (!formData.files.packingLists) newErrors.packingLists = 'Packing Lists is required'
    if (!formData.files.billOfLading) newErrors.billOfLading = 'Bill Of Lading is required'

    if (formData.clearingAgentNum) {
      const digitsOnly = formData.clearingAgentNum.replace(/\D/g, '')
      if (digitsOnly.length !== 11) newErrors.clearingAgentNum = 'Clearing agent number must be exactly 11 digits'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    // include customer info if provided
    onSubmit({ ...formData, customer: customerInfo })
  }

  const handleFileChange = async (fileType, file, existingDocId) => {
      // Ensure draft exists before uploading
    if (file && ensureDraft) {
      try {
        await ensureDraft();  // this will create draft and notify parent of userCreated/password
      } catch (err) {
        console.error('Failed to create draft', err);
        return;
      }
    }
    updateFormData((prev) => ({ ...prev, files: { ...prev.files, [fileType]: file } }))

    if (errors[fileType]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[fileType]
        return newErrors
      })
    }

    if (file && onDocumentUpload) {
      try {
        setUploadSuccess((prev) => ({ ...prev, [fileType]: '' }))
        setUploadProgress((prev) => ({ ...prev, [fileType]: 0 }))
        await onDocumentUpload(file, fileType, existingDocId, (percent) => {
          setUploadProgress((prev) => ({ ...prev, [fileType]: percent }))
        })
        setUploadSuccess((prev) => ({ ...prev, [fileType]: 'Upload complete' }))
        setUploadProgress((prev) => ({ ...prev, [fileType]: 100 }))
      } catch (err) {
        setUploadSuccess((prev) => ({ ...prev, [fileType]: `Upload failed: ${err.message}` }))
        setUploadProgress((prev) => { const updated = { ...prev }; delete updated[fileType]; return updated })
        setErrors((prev) => ({ ...prev, [fileType]: err.message }))
        updateFormData((prev) => { const newFiles = { ...prev.files }; delete newFiles[fileType]; return { ...prev, files: newFiles } })
      }
    } else if (file) {
      setUploadSuccess((prev) => ({ ...prev, [fileType]: 'File selected' }))
    } else {
      setUploadSuccess((prev) => { const newSuccess = { ...prev }; delete newSuccess[fileType]; return newSuccess })
    }
  }

  const handleClearingAgentNumChange = (e) => {
    const value = e.target.value
    const digitsOnly = value.replace(/\D/g, '')
    const limitedValue = digitsOnly.slice(0, 11)
    updateFormData((prev) => ({ ...prev, clearingAgentNum: limitedValue }), { notify: false })
    if (errors.clearingAgentNum) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.clearingAgentNum
        return newErrors
      })
    }
  }

  const handleClearingAgentNumBlur = (e) => {
    const value = e.target.value
    const digitsOnly = value.replace(/\D/g, '')
    const limitedValue = digitsOnly.slice(0, 11)
    const next = { ...formData, clearingAgentNum: limitedValue }
    updateFormData(next, { notify: false })
    commitUserChanges(next)
  }

  const handleCityChange = (city) => {
    updateFormData((prev) => ({ ...prev, city, containerType: city === 'LHR' ? 'LCL' : prev.containerType }))
  }

  const handleFieldChange = (field, value) => updateFormData((prev) => ({ ...prev, [field]: value }))

  return (
    <form onSubmit={handleSubmit} className="space-y-6 frm-elem">
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center space-x-2">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>Import Documentation</span>
        </h3>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Select City</label>
          <div className="inline-flex rounded-full bg-gray-100 p-1">
            <button type="button" onClick={() => handleCityChange('LHR')} className={`px-6 py-3 text-base rounded-full font-medium ${formData.city === 'LHR' ? 'bg-[#2c3e50] text-white' : 'text-gray-700'}`}>LHR</button>
            <button type="button" onClick={() => handleCityChange('KHI')} className={`ml-1 px-6 py-3 text-base rounded-full font-medium ${formData.city === 'KHI' ? 'bg-[#2c3e50] text-white' : 'text-gray-700'}`}>KHI</button>
          </div>
        </div>

        <div className="mb-6 trns-mode">
          <label className="block text-sm font-medium text-slate-700 mb-3">Transport Mode</label>
          {formData.city === 'LHR' ? (
            <div className="radio-option">
              <input type="radio" id="exp-air-only" name="exp-transport" value="air" checked={formData.transportMode === 'air'} onChange={(e) => handleFieldChange('transportMode', e.target.value)} />
              <label htmlFor="exp-air-only">By air only</label>
            </div>
          ) : (
            <div>
              <div className="radio-option">
                <input type="radio" id="exp-air" name="exp-transport" value="air" checked={formData.transportMode === 'air'} onChange={(e) => handleFieldChange('transportMode', e.target.value)} />
                <label htmlFor="exp-air">By Air</label>
              </div>
              <div className="radio-option">
                <input type="radio" id="exp-sea" name="exp-transport" value="sea" checked={formData.transportMode === 'sea'} onChange={(e) => handleFieldChange('transportMode', e.target.value)} />
                <label htmlFor="exp-sea">By Sea</label>
              </div>
            </div>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Shipment Type</label>
          <div className="inline-flex rounded-full bg-gray-100 p-1">
            <button type="button" onClick={() => handleFieldChange('containerType', 'LCL')} className={`px-6 py-3 rounded-full font-medium ${formData.containerType === 'LCL' ? 'bg-[#2c3e50] text-white' : 'text-gray-700'}`}>LCL</button>
            <button type="button" disabled={formData.city === 'LHR'} onClick={() => handleFieldChange('containerType', 'FCL')} className={`ml-1 px-6 py-3 rounded-full font-medium ${formData.containerType === 'FCL' ? 'bg-[#2c3e50] text-white' : 'text-gray-700'} ${formData.city === 'LHR' ? 'opacity-50 cursor-not-allowed' : ''}`}>FCL</button>
          </div>
        </div>

        {formData.city === 'KHI' && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-3">Port</label>
            <select value={formData.port} onChange={(e) => handleFieldChange('port', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500" style={{ padding: '8px 10px' }}>
              <option value="">Select a port/Airport</option>
              <option value="KGT/PICT">KGT/PICT</option>
              <option value="KICT">KICT</option>
              <option value="SAPT">SAPT</option>
              <option value="Port Qasim">Port Qasim</option>
              <option value="NLC">NLC</option>
              <option value="Burwa Oil">Burwa Oil</option>
              <option value="Al Hamad">Al Hamad</option>
              <option value="Pak Scaleen">Pak Scaleen</option>
              <option value="ODT">ODT</option>
              <option value="Airport">Airport</option>
            </select>
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Clearing Agent Number</label>
          <input type="text" value={formData.clearingAgentNum} onChange={handleClearingAgentNumChange} onBlur={handleClearingAgentNumBlur} placeholder="Enter 11-digit clearing agent number" maxLength={11} inputMode="numeric" pattern="[0-9]{11}" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500" style={{ padding: '8px 10px' }} />
          {errors.clearingAgentNum && <p className="text-red-600 text-sm mt-1 error-msg" style={{ color: 'red' }}>{errors.clearingAgentNum}</p>}
        </div>

        <div className="mb-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4">Required Documents</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              {existingDocuments.commonInvoices && !formData.files.commonInvoices && (
                <div className="text-xs text-slate-600 mb-1">Current: {existingDocuments.commonInvoices.fileName || 'Document'}{' '}
                  {existingDocuments.commonInvoices.signedUrl || existingDocuments.commonInvoices.fileUrl ? (
                    <a href={existingDocuments.commonInvoices.signedUrl || existingDocuments.commonInvoices.fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Download</a>
                  ) : null}
                </div>
              )}
              <FileUpload required label="Commercial Invoices" onChange={(file) => handleFileChange('commonInvoices', file, existingDocuments.commonInvoices?.id)} selectedFile={formData.files.commonInvoices} />
              {errors.commonInvoices && <p className="text-red-600 text-sm mt-1 error-msg" style={{ color: 'red' }}>{errors.commonInvoices}</p>}
              {uploadingDocs.commonInvoices && <p className="text-blue-600 text-sm mt-1">Uploading...</p>}
              {renderProgress('commonInvoices')}
              {uploadSuccess.commonInvoices && !uploadingDocs.commonInvoices && <p className="text-green-600 text-sm mt-1 success-msge" style={{ color: 'green' }}>{uploadSuccess.commonInvoices}</p>}
            </div>

            <div>
              {existingDocuments.packingLists && !formData.files.packingLists && (
                <div className="text-xs text-slate-600 mb-1">Current: {existingDocuments.packingLists.fileName || 'Document'}{' '}
                  {existingDocuments.packingLists.signedUrl || existingDocuments.packingLists.fileUrl ? (
                    <a href={existingDocuments.packingLists.signedUrl || existingDocuments.packingLists.fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Download</a>
                  ) : null}
                </div>
              )}
              <FileUpload required label="Packing Lists" onChange={(file) => handleFileChange('packingLists', file, existingDocuments.packingLists?.id)} selectedFile={formData.files.packingLists} />
              {errors.packingLists && <p className="text-red-600 text-sm mt-1 error-msg" style={{ color: 'red' }}>{errors.packingLists}</p>}
              {uploadingDocs.packingLists && <p className="text-blue-600 text-sm mt-1">Uploading...</p>}
              {renderProgress('packingLists')}
              {uploadSuccess.packingLists && !uploadingDocs.packingLists && <p className="text-green-600 text-sm mt-1 success-msge" style={{ color: 'green' }}>{uploadSuccess.packingLists}</p>}
            </div>

            <div>
              {existingDocuments.billOfLading && !formData.files.billOfLading && (
                <div className="text-xs text-slate-600 mb-1">Current: {existingDocuments.billOfLading.fileName || 'Document'}{' '}
                  {existingDocuments.billOfLading.signedUrl || existingDocuments.billOfLading.fileUrl ? (
                    <a href={existingDocuments.billOfLading.signedUrl || existingDocuments.billOfLading.fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Download</a>
                  ) : null}
                </div>
              )}
              <FileUpload required label="Bill Of Lading" onChange={(file) => handleFileChange('billOfLading', file, existingDocuments.billOfLading?.id)} selectedFile={formData.files.billOfLading} />
              {errors.billOfLading && <p className="text-red-600 text-sm mt-1 error-msg" style={{ color: 'red' }}>{errors.billOfLading}</p>}
              {uploadingDocs.billOfLading && <p className="text-blue-600 text-sm mt-1">Uploading...</p>}
              {renderProgress('billOfLading')}
              {uploadSuccess.billOfLading && !uploadingDocs.billOfLading && <p className="text-green-600 text-sm mt-1 success-msge" style={{ color: 'green' }}>{uploadSuccess.billOfLading}</p>}
            </div>

            <div>
              {existingDocuments.insurance && !formData.files.insurance && (
                <div className="text-xs text-slate-600 mb-1">Current: {existingDocuments.insurance.fileName || 'Document'}{' '}
                  {existingDocuments.insurance.signedUrl || existingDocuments.insurance.fileUrl ? (
                    <a href={existingDocuments.insurance.signedUrl || existingDocuments.insurance.fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Download</a>
                  ) : null}
                </div>
              )}
              <FileUpload label="Insurance" onChange={(file) => handleFileChange('insurance', file, existingDocuments.insurance?.id)} selectedFile={formData.files.insurance} />
              {uploadingDocs.insurance && <p className="text-blue-600 text-sm mt-1">Uploading...</p>}
              {renderProgress('insurance')}
              {uploadSuccess.insurance && !uploadingDocs.insurance && <p className="text-green-600 text-sm mt-1 success-msge" style={{ color: 'green' }}>{uploadSuccess.insurance}</p>}
            </div>

            <div>
              {existingDocuments.others && !formData.files.others && (
                <div className="text-xs text-slate-600 mb-1">Current: {existingDocuments.others.fileName || 'Document'}{' '}
                  {existingDocuments.others.signedUrl || existingDocuments.others.fileUrl ? (
                    <a href={existingDocuments.others.signedUrl || existingDocuments.others.fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Download</a>
                  ) : null}
                </div>
              )}
              <FileUpload label="Others" onChange={(file) => handleFileChange('others', file, existingDocuments.others?.id)} selectedFile={formData.files.others} />
              {uploadingDocs.others && <p className="text-blue-600 text-sm mt-1">Uploading...</p>}
              {renderProgress('others')}
              {uploadSuccess.others && !uploadingDocs.others && <p className="text-green-600 text-sm mt-1 success-msge" style={{ color: 'green' }}>{uploadSuccess.others}</p>}
            </div>
          </div>
        </div>

        <button type="submit" disabled={disabled} className="w-full btn-modal-sub bg-slate-600 hover:bg-slate-800 text-white py-3 px-6 rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed">{disabled ? 'Submitting...' : 'Submit Import Documentation'}</button>
      </div>
    </form>
  )
}
